/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  accessAPI,
  formatFecha,
  Titulo,
  Boton,
  CampoInformacion,
  TablaFiltrable,
  tituloBase,
  usePermiso
} from "../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const FRECUENCIA_LABEL = {
  SEMANAL: "Semanal",
  MENSUAL: "Mensual",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual",
  BIENAL: "Bienal",
  TRIENAL: "Trienal",
};
const frecuenciaEtiqueta = (v) =>
  v ? FRECUENCIA_LABEL[v] || v : "Por actividad";

export default function PropuestaSabana() {
  const { propuestaId } = useParams();
  const navigate = useNavigate();

  const [propuesta, setPropuesta] = useState(null);
  const [certificadoTipos, setCertificadoTipos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [nombre, setNombre] = useState("");
  const [masiva, setMasiva] = useState(true);
  const [notas, setNotas] = useState("");
  const [notasInternas, setNotasInternas] = useState("");
  const [items, setItems] = useState([]);
  const [adicionales, setAdicionales] = useState([]);
  const [nuevoAdicionalDescripcion, setNuevoAdicionalDescripcion] =
    useState("");
  const [nuevoAdicionalPrecio, setNuevoAdicionalPrecio] = useState(null);
  const [nuevoAdicionalMonedaId, setNuevoAdicionalMonedaId] = useState(null);
  const [loader, setLoader] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cambiandoActiva, setCambiandoActiva] = useState(false);

  const toast = useRef(null);

  // Con lectura de facturación la propuesta se ve, sin poder editarla ni
  // activarla o desactivarla
  const { puedeEditar } = usePermiso("admin.facturacion");
  const editable =
    puedeEditar && !!propuesta && !propuesta.fechaAceptacion && propuesta.activa;
  const puedeToggleActiva = puedeEditar && !!propuesta;

  function cargarPropuesta(onLoaded) {
    accessAPI(
      "GET",
      `admin/propuesta/sabana/${propuestaId}`,
      null,
      (respuesta) => {
        setPropuesta(respuesta);
        setNombre(respuesta.nombre || "");
        setMasiva(respuesta.masiva ?? true);
        setNotas(respuesta.notas || "");
        setNotasInternas(respuesta.notasInternas || "");
        setItems(
          (respuesta.propuestaSabanaCertificadoTipos || []).map((r) => ({
            certificadoTipoId: r.certificadoTipoId,
            actividadTipoId: r.actividadTipoId,
            precioMonto: r.precioMonto,
            precioMonedaId: r.precioMonedaId,
            certificadoTipoNombre: r.certificadoTipo?.nombre || "",
            actividadTipoNombre: r.actividadTipo?.nombre || "",
            frecuenciaRefacturacion: r.frecuenciaRefacturacion || null,
            precioMoneda: r.precioMoneda || null,
          }))
        );
        setAdicionales(
          Array.isArray(respuesta.adicionales) ? respuesta.adicionales : []
        );
        onLoaded?.(respuesta);
      },
      (respuesta) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar propuesta",
          sticky: true,
        });
      }
    );
  }

  useEffect(() => {
    cargarPropuesta((resp) => {
      // Sólo cargamos referencia + monedas si la propuesta es editable
      // (no aceptada y activa). En los demás casos la vista es de lectura.
      if (resp.fechaAceptacion || !resp.activa) {
        setLoader(false);
        return;
      }
      let pendientes = 2;
      const done = () => {
        pendientes -= 1;
        if (pendientes === 0) setLoader(false);
      };
      accessAPI(
        "GET",
        "admin/propuesta/sabana/referencia",
        null,
        (r) => {
          setCertificadoTipos(r || []);
          done();
        },
        () => done()
      );
      accessAPI(
        "GET",
        "admin/config/moneda?dropdown=true",
        null,
        (r) => {
          setMonedas(r || []);
          if (r?.length > 0) {
            setNuevoAdicionalMonedaId(r[0].value);
          }
          done();
        },
        () => done()
      );
    });
    document.title = "Propuesta sábana";
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propuestaId]);

  const certificadoTiposAgregadosIds = [
    ...new Set(items.map((i) => i.certificadoTipoId)),
  ];

  const certificadoTipoOptions = certificadoTipos
    .filter((ct) => !certificadoTiposAgregadosIds.includes(ct.id))
    .map((ct) => ({ label: ct.nombre, value: ct.id }));

  function agregarCertificadoTipo(certificadoTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return;
    const nuevos = (ct.actividadtipo_x_certificadotipo || []).map((x) => ({
      certificadoTipoId: ct.id,
      actividadTipoId: x.actividadtipoid,
      precioMonto: x.precio || 0,
      precioMonedaId: x.moneda?.id || monedas[0]?.value || null,
      certificadoTipoNombre: ct.nombre,
      actividadTipoNombre: x.actividadtipo?.nombre || "",
    }));
    setItems((prev) => [...prev, ...nuevos]);
  }

  function actividadTipoOptionsParaCertificado(certificadoTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return [];
    const yaPresentes = items
      .filter((i) => i.certificadoTipoId === certificadoTipoId)
      .map((i) => i.actividadTipoId);
    return (ct.actividadtipo_x_certificadotipo || [])
      .filter((x) => !yaPresentes.includes(x.actividadtipoid))
      .map((x) => ({
        label: x.actividadtipo?.nombre || `#${x.actividadtipoid}`,
        value: x.actividadtipoid,
      }));
  }

  function agregarActividad(certificadoTipoId, actividadTipoId) {
    const ct = certificadoTipos.find((c) => c.id === certificadoTipoId);
    if (!ct) return;
    const x = (ct.actividadtipo_x_certificadotipo || []).find(
      (a) => a.actividadtipoid === actividadTipoId
    );
    if (!x) return;
    setItems((prev) => [
      ...prev,
      {
        certificadoTipoId: ct.id,
        actividadTipoId: x.actividadtipoid,
        precioMonto: x.precio || 0,
        precioMonedaId: x.moneda?.id || monedas[0]?.value || null,
        certificadoTipoNombre: ct.nombre,
        actividadTipoNombre: x.actividadtipo?.nombre || "",
      },
    ]);
  }

  function eliminarItem(certificadoTipoId, actividadTipoId) {
    setItems((prev) =>
      prev.filter(
        (i) =>
          !(
            i.certificadoTipoId === certificadoTipoId &&
            i.actividadTipoId === actividadTipoId
          )
      )
    );
  }

  function eliminarCertificadoTipo(certificadoTipoId) {
    setItems((prev) =>
      prev.filter((i) => i.certificadoTipoId !== certificadoTipoId)
    );
  }

  function actualizarPrecio(certificadoTipoId, actividadTipoId, precio) {
    setItems((prev) =>
      prev.map((i) =>
        i.certificadoTipoId === certificadoTipoId &&
        i.actividadTipoId === actividadTipoId
          ? { ...i, precioMonto: precio }
          : i
      )
    );
  }

  function actualizarMoneda(certificadoTipoId, actividadTipoId, monedaId) {
    setItems((prev) =>
      prev.map((i) =>
        i.certificadoTipoId === certificadoTipoId &&
        i.actividadTipoId === actividadTipoId
          ? { ...i, precioMonedaId: monedaId }
          : i
      )
    );
  }

  function referenciaParaItem(item) {
    const ct = certificadoTipos.find((c) => c.id === item.certificadoTipoId);
    if (!ct) return null;
    const x = (ct.actividadtipo_x_certificadotipo || []).find(
      (a) => a.actividadtipoid === item.actividadTipoId
    );
    if (!x || x.precio == null) return null;
    return `Ref: ${x.moneda?.simbolo || ""} ${x.precio}`;
  }

  function agregarAdicional() {
    if (
      !nuevoAdicionalDescripcion.trim() ||
      !nuevoAdicionalPrecio ||
      nuevoAdicionalPrecio <= 0 ||
      !nuevoAdicionalMonedaId
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Complete todos los campos del adicional",
        sticky: true,
      });
      return;
    }
    const monedaSel = monedas.find((m) => m.value === nuevoAdicionalMonedaId);
    setAdicionales((prev) => [
      ...prev,
      {
        descripcion: nuevoAdicionalDescripcion.trim(),
        precio: nuevoAdicionalPrecio,
        monedaid: nuevoAdicionalMonedaId,
        moneda: monedaSel
          ? {
              id: nuevoAdicionalMonedaId,
              nombre: monedaSel.etiqueta,
              simbolo: monedaSel.simbolo || "",
            }
          : null,
      },
    ]);
    setNuevoAdicionalDescripcion("");
    setNuevoAdicionalPrecio(null);
  }

  function eliminarAdicional(index) {
    setAdicionales((prev) => prev.filter((_, i) => i !== index));
  }

  function guardarCambios() {
    if (!nombre.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "El nombre no puede estar vacío",
        sticky: true,
      });
      return;
    }
    if (items.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Agregue al menos un item",
        sticky: true,
      });
      return;
    }
    const sinPrecio = items.some(
      (i) => !i.precioMonto || i.precioMonto <= 0 || !i.precioMonedaId
    );
    if (sinPrecio) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Todos los items deben tener precio y moneda",
        sticky: true,
      });
      return;
    }

    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/propuesta/sabana/${propuestaId}`,
      {
        nombre: nombre.trim(),
        masiva,
        notas,
        notasInternas,
        items: items.map((i) => ({
          certificadoTipoId: i.certificadoTipoId,
          actividadTipoId: i.actividadTipoId,
          precioMonto: i.precioMonto,
          precioMonedaId: i.precioMonedaId,
        })),
        adicionales,
      },
      (respuesta) => {
        setGuardando(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Propuesta actualizada",
        });
        cargarPropuesta();
      },
      (respuesta) => {
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "Error al guardar",
          sticky: true,
        });
      }
    );
  }

  function toggleActiva() {
    const nuevoEstado = !propuesta.activa;
    confirmDialog({
      message: nuevoEstado
        ? "¿Reactivar esta propuesta?"
        : "¿Desactivar esta propuesta? Dejará de aparecer en la lista del cliente.",
      header: "Confirmar",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: nuevoEstado ? "Reactivar" : "Desactivar",
      rejectLabel: "Cancelar",
      acceptClassName: nuevoEstado ? "" : "p-button-danger",
      accept: () => {
        setCambiandoActiva(true);
        accessAPI(
          "PUT",
          `admin/propuesta/sabana/${propuestaId}`,
          { activa: nuevoEstado },
          () => {
            setCambiandoActiva(false);
            if (!nuevoEstado) {
              navigate(`/clientes/${propuesta.cliente?.id}/facturas`);
            } else {
              cargarPropuesta();
            }
          },
          (respuesta) => {
            setCambiandoActiva(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al actualizar",
              sticky: true,
            });
          }
        );
      },
    });
  }

  if (loader) {
    return (
      <div>
        <Skeleton width="300px" height="2rem" css={{ marginBottom: "1rem" }} />
        <Card>
          <Skeleton width="100%" height="3rem" css={{ marginBottom: "0.5rem" }} />
          <Skeleton width="100%" height="3rem" />
        </Card>
      </div>
    );
  }

  if (!propuesta) return null;

  // Agrupa items por certificadoTipo preservando orden de aparición
  const grupos = [];
  const vistos = new Set();
  for (const item of items) {
    if (!vistos.has(item.certificadoTipoId)) {
      vistos.add(item.certificadoTipoId);
      grupos.push({
        certificadoTipoId: item.certificadoTipoId,
        certificadoTipoNombre: item.certificadoTipoNombre,
        items: items.filter(
          (i) => i.certificadoTipoId === item.certificadoTipoId
        ),
      });
    }
  }

  const etiquetas = [];
  if (propuesta.fechaAceptacion) {
    etiquetas.push({ texto: "Aceptada", ok: true });
  } else {
    etiquetas.push({ texto: "Emitida", alerta: true });
  }
  if (!propuesta.activa) {
    etiquetas.push({ texto: "Desactivada", error: true });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      <Titulo
        urlVolver={`/clientes/${propuesta.cliente?.id}/facturas`}
        etiquetas={etiquetas}
        botones={
          puedeToggleActiva ? (
            <>
              <Boton
                rojo={propuesta.activa}
                transparente
                callback={toggleActiva}
                loader={cambiandoActiva}
              >
                {propuesta.activa ? "Desactivar" : "Reactivar"}
              </Boton>
              {editable && (
                <Boton callback={guardarCambios} loader={guardando}>
                  Guardar cambios
                </Boton>
              )}
            </>
          ) : null
        }
      >
        {nombre || "Propuesta sábana"}
        {propuesta.cliente?.razonsocial
          ? ` - ${propuesta.cliente.razonsocial}`
          : ""}
      </Titulo>

      {propuesta.fechaAceptacion && (
        <Card
          css={{
            marginBottom: "1rem",
            backgroundColor: "var(--green-50)",
            border: "1px solid var(--green-200)",
          }}
        >
          <div
            css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <i
              className="pi pi-check-circle"
              css={{ fontSize: "1.5rem", color: "var(--green-600)" }}
            />
            <div>
              <div css={{ fontWeight: "bold", color: "var(--green-700)" }}>
                Propuesta aceptada
              </div>
              <div css={{ color: "var(--green-600)" }}>
                Aceptada por{" "}
                {propuesta.nombreAceptador || "-"}{" "}
                el {formatFecha(propuesta.fechaAceptacion)}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <Titulo>Detalles</Titulo>
        <div className="grid">
          {editable ? (
            <CampoInformacion nombreCampo="Nombre" anchoCompleto>
              <InputText
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                css={{ width: "100%", maxWidth: "500px" }}
              />
            </CampoInformacion>
          ) : (
            <CampoInformacion nombreCampo="Nombre">
              {propuesta.nombre || "-"}
            </CampoInformacion>
          )}
          <CampoInformacion nombreCampo="Cliente">
            {propuesta.cliente?.razonsocial || "-"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Propuesta más IVA">
            {editable ? (
              <InputSwitch
                checked={masiva}
                onChange={(e) => setMasiva(e.value)}
              />
            ) : propuesta.masiva ? (
              "Sí"
            ) : (
              "No"
            )}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Fecha de emisión">
            {formatFecha(propuesta.fechaEmision) || "-"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Fecha de aceptación">
            {formatFecha(propuesta.fechaAceptacion) || "-"}
          </CampoInformacion>
          {propuesta.usuarioemisor && (
            <CampoInformacion nombreCampo="Emisor">
              {`${propuesta.usuarioemisor.nombre || ""} ${propuesta.usuarioemisor.apellido || ""}`.trim()}
            </CampoInformacion>
          )}
          {propuesta.nombreAceptador && (
            <CampoInformacion nombreCampo="Aceptador">
              {propuesta.nombreAceptador}
            </CampoInformacion>
          )}
        </div>
      </Card>

      {grupos.length === 0 && (
        <Card css={{ marginTop: "1rem" }}>
          <p css={{ color: "var(--grisTextos)" }}>
            {editable
              ? "Agregue al menos un tipo de certificado."
              : "Sin items."}
          </p>
        </Card>
      )}

      {grupos.map((grupo) => (
        <Card key={grupo.certificadoTipoId} css={{ marginTop: "1rem" }}>
          <Titulo
            botones={
              editable ? (
                <Boton
                  texto
                  rojo
                  chico
                  callback={() =>
                    eliminarCertificadoTipo(grupo.certificadoTipoId)
                  }
                >
                  <i className="pi pi-times" /> Quitar certificado
                </Boton>
              ) : null
            }
          >
            {grupo.certificadoTipoNombre || "Actividades"}
          </Titulo>

          <TablaFiltrable conFiltros={false}
            value={grupo.items}
            size="small"
            dataKey="actividadTipoId"
            nombreArchivo="items_propuesta_sabana"
          >
            <Column
              header="Actividad"
              body={(item) => {
                const ref = editable ? referenciaParaItem(item) : null;
                return (
                  <div>
                    <div>{item.actividadTipoNombre}</div>
                    {ref && (
                      <div
                        css={{
                          color: "var(--grisTextos)",
                          fontSize: "0.85rem",
                        }}
                      >
                        {ref}
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <Column
              header="Precio"
              body={(item) =>
                editable ? (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <InputNumber
                      value={item.precioMonto}
                      onValueChange={(e) =>
                        actualizarPrecio(
                          item.certificadoTipoId,
                          item.actividadTipoId,
                          e.value || 0
                        )
                      }
                      mode="decimal"
                      minFractionDigits={0}
                      inputStyle={{ width: "90px" }}
                    />
                    <Dropdown
                      value={item.precioMonedaId}
                      onChange={(e) =>
                        actualizarMoneda(
                          item.certificadoTipoId,
                          item.actividadTipoId,
                          e.value
                        )
                      }
                      options={monedas}
                      optionLabel="etiqueta"
                      optionValue="value"
                      style={{ width: "130px" }}
                    />
                  </div>
                ) : (
                  `${item.precioMoneda?.simbolo || ""} ${item.precioMonto}${item.precioMoneda?.nombre ? ` (${item.precioMoneda.nombre})` : ""}`
                )
              }
            />
            <Column
              header="Facturación"
              body={(item) => frecuenciaEtiqueta(item.frecuenciaRefacturacion)}
            />
            {editable && (
              <Column
                style={{ width: "3rem", textAlign: "center" }}
                body={(item) => (
                  <Boton
                    texto
                    rojo
                    chico
                    callback={() =>
                      eliminarItem(
                        item.certificadoTipoId,
                        item.actividadTipoId
                      )
                    }
                  >
                    <i className="pi pi-times" />
                  </Boton>
                )}
              />
            )}
          </TablaFiltrable>

          {editable &&
            actividadTipoOptionsParaCertificado(grupo.certificadoTipoId)
              .length > 0 && (
              <div css={{ marginTop: "0.75rem" }}>
                <Dropdown
                  value={null}
                  onChange={(e) =>
                    agregarActividad(grupo.certificadoTipoId, e.value)
                  }
                  options={actividadTipoOptionsParaCertificado(
                    grupo.certificadoTipoId
                  )}
                  placeholder="Agregar actividad"
                  style={{ width: "100%", maxWidth: "300px" }}
                />
              </div>
            )}
        </Card>
      ))}

      {editable && (
        <Card css={{ marginTop: "1rem" }}>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <span css={{ fontWeight: "bold" }}>
              Agregar tipo de certificado:
            </span>
            <Dropdown
              value={null}
              onChange={(e) => agregarCertificadoTipo(e.value)}
              options={certificadoTipoOptions}
              placeholder="Seleccionar"
              filter
              style={{ minWidth: "280px" }}
              disabled={certificadoTipoOptions.length === 0}
            />
          </div>
        </Card>
      )}

      {(editable || adicionales.length > 0) && (
        <Card css={{ marginTop: "1rem" }}>
          <Titulo>Adicionales</Titulo>
          {adicionales.length === 0 && !editable && (
            <p css={{ color: "var(--grisTextos)" }}>Sin adicionales.</p>
          )}
          {adicionales.length > 0 && (
            <div css={{ marginBottom: editable ? "1rem" : 0 }}>
              {adicionales.map((a, index) => (
                <div
                  key={index}
                  css={{
                    padding: "0.75rem 0",
                    borderBottom: "1px solid var(--surface-200)",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div css={{ flex: 1, minWidth: "200px" }}>{a.descripcion}</div>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span css={{ fontWeight: "bold" }}>{a.precio}</span>
                    <span css={{ color: "var(--grisTextos)" }}>
                      {a.moneda?.simbolo || a.moneda?.nombre || ""}
                    </span>
                  </div>
                  {editable && (
                    <Boton
                      texto
                      rojo
                      chico
                      callback={() => eliminarAdicional(index)}
                    >
                      <i className="pi pi-times" />
                    </Boton>
                  )}
                </div>
              ))}
            </div>
          )}
          {editable && (
            <div
              css={{
                display: "flex",
                alignItems: "flex-end",
                gap: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              <div css={{ flex: 1, minWidth: "200px" }}>
                <label
                  css={{
                    display: "block",
                    fontSize: "0.85rem",
                    color: "var(--grisTextos)",
                    marginBottom: "0.25rem",
                  }}
                >
                  Nombre
                </label>
                <InputText
                  value={nuevoAdicionalDescripcion}
                  onChange={(e) =>
                    setNuevoAdicionalDescripcion(e.target.value)
                  }
                  css={{ width: "100%" }}
                />
              </div>
              <div>
                <label
                  css={{
                    display: "block",
                    fontSize: "0.85rem",
                    color: "var(--grisTextos)",
                    marginBottom: "0.25rem",
                  }}
                >
                  Monto
                </label>
                <InputNumber
                  value={nuevoAdicionalPrecio}
                  onValueChange={(e) => setNuevoAdicionalPrecio(e.value)}
                  mode="decimal"
                  minFractionDigits={0}
                  inputStyle={{ width: "100px" }}
                />
              </div>
              <div>
                <label
                  css={{
                    display: "block",
                    fontSize: "0.85rem",
                    color: "var(--grisTextos)",
                    marginBottom: "0.25rem",
                  }}
                >
                  Moneda
                </label>
                <Dropdown
                  value={nuevoAdicionalMonedaId}
                  onChange={(e) => setNuevoAdicionalMonedaId(e.value)}
                  options={monedas}
                  optionLabel="etiqueta"
                  optionValue="value"
                  style={{ width: "180px" }}
                />
              </div>
              <Boton texto callback={agregarAdicional}>
                Agregar
              </Boton>
            </div>
          )}
        </Card>
      )}

      <Card css={{ marginTop: "1rem" }}>
        <Titulo>Notas</Titulo>
        {editable ? (
          <>
            <div css={{ marginBottom: "1rem" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em",
                }}
              >
                Notas para el cliente
              </label>
              <InputTextarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={3}
                autoResize
                css={{ width: "100%" }}
              />
            </div>
            <div>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em",
                }}
              >
                Notas internas (no se muestran al cliente)
              </label>
              <InputTextarea
                value={notasInternas}
                onChange={(e) => setNotasInternas(e.target.value)}
                rows={3}
                autoResize
                css={{ width: "100%" }}
              />
            </div>
          </>
        ) : (
          <div className="grid">
            <CampoInformacion nombreCampo="Notas para el cliente" anchoCompleto>
              <span css={{ whiteSpace: "pre-wrap" }}>
                {propuesta.notas || "-"}
              </span>
            </CampoInformacion>
            <CampoInformacion nombreCampo="Notas internas" anchoCompleto>
              <span css={{ whiteSpace: "pre-wrap" }}>
                {propuesta.notasInternas || "-"}
              </span>
            </CampoInformacion>
          </div>
        )}
      </Card>

    </>
  );
}
